// Data-access layer for the presence channel — supabase provider (D1 = table).
//
// Schema (Postgres): presence(code, user_id, handle, avatar_seed, public_ip,
// device_type, last_seen) PK (code, user_id). Best-effort, like Firestore: a
// missed heartbeat just ages the entry out of the active window. Writes never
// surface errors to the user (try/catch swallowed).
import { getClient } from './client.js';
import { maintainCollection } from './realtime.js';

// Activity windows — identical to the Firestore impl.
export const PRESENCE_ACTIVE_MS = 180_000; // 3 min — past this we drop the row
export const PRESENCE_HEARTBEAT_MS = 25_000; // 25 s — refresh cadence

// Permitted optional Beam fields (F10.1/F10.2): publicIp → public_ip,
// deviceType → device_type. Unknown keys are dropped so the schema can't drift.
const ALLOWED_EXTRAS = new Set(['publicIp', 'deviceType']);

function sanitizeExtras(extras) {
  if (!extras || typeof extras !== 'object') return null;
  const out = {};
  let hasAny = false;
  for (const [key, value] of Object.entries(extras)) {
    if (!ALLOWED_EXTRAS.has(key)) continue;
    if (value === undefined) continue;
    // camelCase contract key → snake_case column.
    if (key === 'publicIp') out.public_ip = value;
    else if (key === 'deviceType') out.device_type = value;
    hasAny = true;
  }
  return hasAny ? out : null;
}

/**
 * Join `code` as `userId` with display metadata. Idempotent upsert keyed on
 * (code, user_id), so it doubles as a heartbeat. Best-effort — swallowed.
 * Optional `extras` lets Beam piggyback publicIp + deviceType.
 */
export async function joinPresence(code, userId, handle, avatarSeed, extras) {
  if (!code || !userId) return;
  const extra = sanitizeExtras(extras);
  try {
    const payload = {
      code,
      user_id: userId,
      handle: handle || '',
      avatar_seed: avatarSeed || userId,
      last_seen: new Date().toISOString(),
    };
    if (extra) Object.assign(payload, extra);
    await getClient()
      .from('presence')
      .upsert(payload, { onConflict: 'code,user_id' });
  } catch {
    /* best-effort — presence loss ages out client-side */
  }
}

/**
 * Heartbeat — refresh last_seen. Re-uses joinPresence so handle / avatarSeed
 * stay current if changed mid-session.
 */
export async function heartbeat(code, userId, handle, avatarSeed, extras) {
  return joinPresence(code, userId, handle, avatarSeed, extras);
}

/**
 * Leave: best-effort delete. If the network is gone, the entry ages out of the
 * active window on watching clients. Swallowed.
 */
export async function leavePresence(code, userId) {
  if (!code || !userId) return;
  try {
    await getClient()
      .from('presence')
      .delete()
      .eq('code', code)
      .eq('user_id', userId);
  } catch {
    /* best-effort */
  }
}

/**
 * Subscribe to presence for `code`. Yields the FULL array of entries inside
 * the active window (contract §3). A row whose last_seen parses to 0 (server
 * write still in flight) is KEPT so the user's own avatar doesn't flash-hide.
 * Returns the idempotent unsubscribe fn. Each entry:
 *   { id, handle, avatarSeed, lastSeen (ms), publicIp, deviceType }.
 */
export function subscribePresence(code, cb) {
  if (!code) return () => {};
  return maintainCollection({
    table: 'presence',
    filter: { column: 'code', value: code },
    mapRow: (row) => {
      // last_seen is an ISO timestamptz string; Date.parse → epoch ms, 0 if
      // null (write in flight). NaN guard keeps the postFilter math sane.
      const ms = row.last_seen != null ? Date.parse(row.last_seen) : 0;
      const lastSeen = Number.isFinite(ms) ? ms : 0;
      return {
        id: row.user_id,
        handle: row.handle || '',
        avatarSeed: row.avatar_seed || row.user_id,
        lastSeen,
        publicIp: typeof row.public_ip === 'string' ? row.public_ip : null,
        deviceType: row.device_type === 'phone' ? 'phone' : 'monitor',
      };
    },
    postFilter: (e) =>
      e.lastSeen === 0 || Date.now() - e.lastSeen <= PRESENCE_ACTIVE_MS,
    cb,
  });
}
